import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TestBadge, EmptyState, ErrorState, ListSkeleton } from "@/components/common";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { LifeBuoy, Plus } from "lucide-react";

const FLOW = ["open", "assigned", "in_progress", "waiting_customer", "escalated", "resolved", "closed"];
const ST_CLS = {
  open: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
  assigned: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
  in_progress: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  waiting_customer: "bg-muted text-muted-foreground",
  escalated: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
  resolved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  closed: "bg-muted text-muted-foreground",
};
const PRIO_CLS = { high: "text-[hsl(var(--danger))]", medium: "text-[hsl(var(--warning))]", low: "text-muted-foreground" };

const TicketDetail = ({ ticket, setTicket, users, onChanged }) => {
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("");
  const [escReason, setEscReason] = useState("");
  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/tickets/${ticket.ticket_id}`, body);
      setTicket(data); onChanged();
      toast.success("Ticket updated");
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && setTicket(null)}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{ticket.ticket_id} — {ticket.subject}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={ST_CLS[ticket.status]}>{ticket.status.replace(/_/g, " ")}</Badge>
            <Badge variant="outline" className="text-[10px]">{ticket.category}</Badge>
            <span className={`text-xs font-medium capitalize ${PRIO_CLS[ticket.priority] || ""}`}>{ticket.priority} priority</span>
            <TestBadge show={ticket.is_test_record} />
            <span className="text-xs text-muted-foreground ml-auto">SLA due {fmtIST(ticket.due_at)}</span>
          </div>
          <p className="text-sm text-muted-foreground">{ticket.description || "No description"}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={ticket.status} onValueChange={(s) => {
                if (s === "escalated") return; // handled below with reason
                if (s === "resolved" && !ticket.resolution && !resolution) { toast.error("Enter a resolution first"); return; }
                patch({ status: s, resolution: resolution || null });
              }}>
                <SelectTrigger data-testid="ticket-status-select" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FLOW.filter((s) => s !== "escalated").map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Assignee</Label>
              <Select value={ticket.assignee_user_id || ""} onValueChange={(u) => patch({ assignee_user_id: u })}>
                <SelectTrigger data-testid="ticket-assignee-select" className="h-9"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Resolution (required to resolve)</Label>
            <Textarea data-testid="ticket-resolution" rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)}
              placeholder={ticket.resolution || "How was this resolved?"} />
          </div>
          <div className="flex gap-2">
            <Input data-testid="ticket-escalate-reason" className="h-9" placeholder="Escalation reason…" value={escReason} onChange={(e) => setEscReason(e.target.value)} />
            <Button data-testid="ticket-escalate-btn" variant="destructive" className="h-9" disabled={!escReason.trim()}
              onClick={() => { patch({ status: "escalated", escalation_reason: escReason }); setEscReason(""); }}>Escalate</Button>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Internal notes</Label>
            {(ticket.notes || []).map((n, i) => (
              <p key={i} className="text-xs p-1.5 rounded bg-muted/50"><b>{n.by}</b> {fmtIST(n.at)}: {n.text}</p>
            ))}
            <div className="flex gap-2">
              <Input data-testid="ticket-note-input" className="h-8 text-sm" placeholder="Add internal note…" value={note} onChange={(e) => setNote(e.target.value)} />
              <Button data-testid="ticket-note-add" size="sm" className="h-8" disabled={!note.trim()}
                onClick={() => { patch({ note }); setNote(""); }}>Add</Button>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            History: {(ticket.history || []).map((h) => h.event).join(" → ")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewTicketDialog = ({ open, setOpen, users, onCreated }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [contact, setContact] = useState(null);
  // 2026-09 fix: priority used to preselect "medium", letting a ticket get
  // created without anyone consciously choosing its severity. Blank ("")
  // here shows the placeholder below and keeps Create disabled until an
  // employee actually picks one — never silently defaulted, on either end.
  const [form, setForm] = useState({ subject: "", description: "", category: "Service", priority: "", department: "Sales", assignee_user_id: null });
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  const create = async () => {
    try {
      await api.post("/tickets", { ...form, contact_id: contact?.contact_id || null });
      toast.success("Ticket created");
      setOpen(false); setForm({ subject: "", description: "", category: "Service", priority: "", department: "Sales", assignee_user_id: null }); setContact(null); setQ("");
      onCreated();
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">New ticket / complaint</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="ticket-subject" placeholder="Subject…" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <Textarea data-testid="ticket-description" rows={3} placeholder="Description…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-3 gap-2">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ticket-category" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Service", "Complaint", "Billing", "Documents", "Visa Process", "General"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="ticket-priority" className="h-9"><SelectValue placeholder="Select priority…" /></SelectTrigger>
              <SelectContent>
                {["low", "medium", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger data-testid="ticket-department" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Sales", "Process", "Accounts", "Management"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Select value={form.assignee_user_id || ""} onValueChange={(v) => setForm({ ...form, assignee_user_id: v })}>
            <SelectTrigger data-testid="ticket-assignee" className="h-9"><SelectValue placeholder="Assign to (optional)" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="space-y-1.5">
            <Label className="text-xs">Link customer (optional)</Label>
            {!contact ? (
              <>
                <Input data-testid="ticket-customer-search" placeholder="Search customer…" value={q} onChange={(e) => setQ(e.target.value)} />
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {results.map((r) => (
                    <button key={r.contact_id} onClick={() => setContact(r)} className="w-full text-left p-1.5 rounded border text-sm hover:bg-muted/50">
                      {r.full_name} <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm">{contact.full_name} <button className="text-xs underline" onClick={() => setContact(null)}>change</button></p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="ticket-create-button" disabled={!form.subject.trim() || !form.priority} onClick={create}>Create ticket</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function SupportPage() {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);   // R7: error + Retry, never an endless skeleton
  const loadToken = useRef(0);                        // a slower answer for a tab already left is dropped
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  // Round 7 Workstream F - TEST tickets hidden unless an admin opts in (visible toggle).
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();

  useEffect(() => { api.get("/users").then((r) => setUsers((r.data.users || r.data).filter((u) => u.is_active !== false))).catch(() => {}); }, []);
  const load = useCallback(() => {
    const token = ++loadToken.current;
    setRows(null); setLoadError(null);
    api.get(withIncludeTest(`/tickets${status !== "all" ? `?status=${status}` : ""}`, includeTest))
      .then((r) => { if (token === loadToken.current) setRows(r.data); })
      .catch((e) => { if (token === loadToken.current) setLoadError(errDetail(e, "The ticket list didn't load")); });
  }, [status, includeTest]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="support-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">Tickets, complaints and escalations with SLA tracking and auditable history.</p>
        </div>
        <IncludeTestToggle className="ml-auto" includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="support-include-test" />
        <Button data-testid="ticket-new-button" className={canInclude ? "h-9" : "ml-auto h-9"} onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New ticket
        </Button>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="ticket-tab-all" value="all">All</TabsTrigger>
          {FLOW.map((s) => <TabsTrigger key={s} data-testid={`ticket-tab-${s}`} value={s}>{s.replace(/_/g, " ")}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Card>
        {loadError ? (
          <ErrorState testId="tickets-error" title="Couldn't load tickets" message={loadError} onRetry={load} />
        ) : !rows ? (
          <ListSkeleton rows={3} testId="tickets-loading" />
        ) : rows.length === 0 ? (
          <EmptyState icon={LifeBuoy} title="No tickets here" hint="Raise a ticket for any customer issue, complaint or internal escalation." testId="tickets-empty" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="hidden md:table-cell">Priority</TableHead>
                <TableHead className="hidden md:table-cell">Assignee</TableHead>
                <TableHead className="hidden lg:table-cell">SLA due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.ticket_id} className="cursor-pointer" data-testid={`ticket-row-${t.ticket_id}`} onClick={() => setSel(t)}>
                  <TableCell className="font-mono text-xs">{t.ticket_id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{t.contact_name || "—"}</span>
                      <TestBadge show={t.is_test_record} />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm max-w-56 truncate">{t.subject}</TableCell>
                  <TableCell className={`hidden md:table-cell text-sm capitalize ${PRIO_CLS[t.priority] || ""}`}>{t.priority}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{users.find((u) => u.user_id === t.assignee_user_id)?.name || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtIST(t.due_at)}</TableCell>
                  <TableCell><Badge variant="outline" className={ST_CLS[t.status]}>{t.status.replace(/_/g, " ")}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {sel && <TicketDetail ticket={sel} setTicket={setSel} users={users} onChanged={load} />}
      <NewTicketDialog open={newOpen} setOpen={setNewOpen} users={users} onCreated={load} />
    </div>
  );
}
